import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  BROADCAST_PRESET_META,
  BROADCAST_VALIDITY_TEXT_MAX,
  assembleBroadcastBody,
  audienceLabel,
  type BroadcastAudience,
  type BroadcastInput,
  type BroadcastSegmentKey,
  type BroadcastValidity,
  type MembershipConfig,
} from '@naya/shared'

import {
  BroadcastFilterBuilder,
  defaultRule,
} from '../components/BroadcastFilterBuilder.js'
import {
  useBroadcastCapabilities,
  useBroadcasts,
  useCancelBroadcast,
  useCreateBroadcast,
  usePreviewCount,
  useRemoveBroadcast,
} from '../hooks/useBroadcasts.js'
import { useBroadcastCap } from '../hooks/useBroadcastCap.js'
import { usePrograms } from '../program/hooks/useProgram.js'
import { OwnerApiError } from '../ownerApi.js'
import type {
  BroadcastRow,
  BroadcastStatus,
} from '../types.js'
import './broadcasts.css'

/**
 * Owner-facing broadcasts page (slice 5 + the segmentation slice).
 *
 * Backend: /api/broadcasts. Worker tick: 60s. A broadcast targets an
 * `audience` — `everyone` (one class-level Message) or any segmented audience
 * (curated preset / custom filter → per-object loop). The composer resolves
 * an exact recipient count on demand via /broadcasts/preview-count.
 *
 * Cadence: admin hard caps still block in the worker; the soft 7-day signal
 * is advisory-only — surfaced here as a non-blocking line, never a gate.
 */

// Owner-facing status buckets layered over the seven raw DB states.
type StatusBucket = 'all' | 'live' | 'pending' | 'removed' | 'failed'

const STATUS_BUCKET_LABELS: Record<StatusBucket, string> = {
  all: 'All',
  live: 'Live',
  pending: 'Pending',
  removed: 'Removed',
  failed: 'Failed',
}

function bucketMatches(status: BroadcastStatus, bucket: StatusBucket): boolean {
  if (bucket === 'all') return true
  if (bucket === 'live') return status === 'sent'
  if (bucket === 'pending') {
    return (
      status === 'pending' || status === 'sending' || status === 'rate_limited'
    )
  }
  if (bucket === 'removed') {
    return status === 'removed' || status === 'removing'
  }
  return status === 'failed'
}

// UI copy for every selectable audience. The 8 curated presets reuse the
// shared meta; the two legacy kinds get their own copy here.
function segmentMeta(key: BroadcastSegmentKey): {
  label: string
  description: string
} {
  if (key === 'everyone') {
    return {
      label: 'Everyone',
      description: 'Every active customer who has your pass.',
    }
  }
  if (key === 'birthday_this_month') {
    return {
      label: 'Birthday this month',
      description: 'Customers whose birthday falls in the current month.',
    }
  }
  return BROADCAST_PRESET_META[key]
}

// The audience descriptor for a chosen segment key.
function audienceForKey(key: BroadcastSegmentKey): BroadcastAudience {
  if (key === 'everyone') return { kind: 'everyone' }
  if (key === 'birthday_this_month') return { kind: 'birthday_this_month' }
  return { kind: 'preset', preset: key }
}

// A broadcast's audience as a human label — handles pre-segmentation rows
// (no `audience`, fall back to the legacy `segment`).
function displayAudience(b: BroadcastRow): string {
  if (b.audience) return audienceLabel(b.audience)
  return b.segment === 'everyone' ? 'Everyone' : 'Birthday this month'
}

export function BroadcastsPage() {
  const listQ = useBroadcasts()
  const capQ = useBroadcastCap()
  const programsQ = usePrograms()
  const [composerOpen, setComposerOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusBucket>('all')

  // Program context for the history (SLICE-PASS-TYPES + MEMBERSHIP). The
  // "latest only" rotation is scoped PER PROGRAM, so when the tenant runs more
  // than one broadcastable program we label each card with its program and
  // clarify the persistence banner. A name map over ALL programs (any status)
  // so even an archived program's past broadcasts still resolve a label.
  const allPrograms = programsQ.data?.programs ?? []
  const programNameById = new Map(allPrograms.map((p) => [p.id, p.name]))
  const multiProgram =
    allPrograms.filter(
      (p) =>
        p.status === 'active' &&
        (p.pass_type === 'loyalty' || p.pass_type === 'membership'),
    ).length > 1

  if (listQ.isLoading) {
    return <p className="owner-loading">Loading…</p>
  }
  if (listQ.error) {
    return (
      <p className="owner-error-state">
        Couldn't load broadcasts. Try refreshing.
      </p>
    )
  }

  const broadcasts = listQ.data?.broadcasts ?? []
  const isInFlight = (s: BroadcastStatus) =>
    s === 'pending' || s === 'sending' || s === 'removing'

  const filteredAll = broadcasts.filter((b) =>
    bucketMatches(b.status, statusFilter),
  )
  const pending = filteredAll.filter((b) => isInFlight(b.status))
  const recent = filteredAll.filter((b) => !isInFlight(b.status))
  const liveCount = broadcasts.filter((b) => b.status === 'sent').length

  return (
    <div className="broadcasts-list">
      <header className="broadcasts-hero">
        <div className="broadcasts-hero-text">
          <p className="broadcasts-eyebrow">Reach your customers</p>
          <h1>Broadcasts</h1>
          <p className="broadcasts-hero-sub">
            Send a push notification straight to your customers' Apple Wallet
            and Google Wallet passes — to everyone, or a targeted segment.
          </p>
        </div>
        <button
          type="button"
          className="owner-btn owner-btn-primary broadcasts-hero-cta"
          onClick={() => setComposerOpen(true)}
        >
          New broadcast
        </button>
      </header>

      <div className="broadcasts-persistence-banner" role="status">
        <span className="broadcasts-banner-mark" aria-hidden="true">
          ✦
        </span>
        <span>
          {/* "Latest only" mirrors KEEP_LIVE_BROADCASTS = 1 in the broadcast
              worker — keep them in sync if that ever changes. The rotation is
              scoped PER PROGRAM (rotateSentBroadcasts filters by program_id), so
              a multi-program tenant gets the per-program wording. */}
          {multiProgram ? (
            <>
              Each program keeps its own latest announcement on the back of its
              passes. Send a new one to a program and it replaces just that
              program's previous message — your other programs are untouched.
            </>
          ) : (
            <>
              Your latest announcement shows on the back of every customer's
              pass. Send a new one and it replaces the previous, so customers
              always see just your most recent message.
            </>
          )}
        </span>
      </div>

      <div className="broadcasts-toolbar">
        <div className="broadcasts-toolbar-meta">
          {capQ.data && (
            <span className="broadcasts-cadence">
              {formatCapMessage(capQ.data)}
            </span>
          )}
          {/* Single-program tenants: KEEP_LIVE_BROADCASTS=1 means this is
              always exactly 0 or 1 — pure noise, so only multi-program
              tenants (where the count is genuinely variable, one per
              program) see it. */}
          {multiProgram && liveCount > 0 && (
            <span className="broadcasts-livecount">
              {liveCount} live on passes
            </span>
          )}
        </div>
        <label className="broadcasts-status-filter">
          <span className="broadcasts-status-filter-label">Show</span>
          <select
            className="owner-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusBucket)}
            aria-label="Filter by status"
          >
            {(Object.keys(STATUS_BUCKET_LABELS) as StatusBucket[]).map((b) => (
              <option key={b} value={b}>
                {STATUS_BUCKET_LABELS[b]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {statusFilter === 'all' ? (
        <>
          {pending.length > 0 && (
            <section className="broadcasts-section">
              <h2 className="broadcast-section-heading">In progress</h2>
              <div className="broadcasts-grid">
                {pending.map((b) => (
                  <BroadcastCard
                  key={b.id}
                  broadcast={b}
                  programName={
                    multiProgram && b.program_id
                      ? (programNameById.get(b.program_id) ?? null)
                      : null
                  }
                />
                ))}
              </div>
            </section>
          )}

          <section className="broadcasts-section">
            <h2 className="broadcast-section-heading">Recent</h2>
            {recent.length === 0 && pending.length === 0 ? (
              <EmptyState onCompose={() => setComposerOpen(true)} />
            ) : recent.length === 0 ? (
              <p className="owner-card-sub">No completed broadcasts yet.</p>
            ) : (
              <div className="broadcasts-grid">
                {recent.map((b) => (
                  <BroadcastCard
                  key={b.id}
                  broadcast={b}
                  programName={
                    multiProgram && b.program_id
                      ? (programNameById.get(b.program_id) ?? null)
                      : null
                  }
                />
                ))}
              </div>
            )}
          </section>
        </>
      ) : (
        <section className="broadcasts-section">
          {filteredAll.length === 0 ? (
            <div className="owner-card owner-empty-state">
              <p className="owner-card-title">
                No {STATUS_BUCKET_LABELS[statusFilter].toLowerCase()} broadcasts
              </p>
              <p className="owner-card-sub">
                Switch to <strong>All</strong> to see every broadcast.
              </p>
            </div>
          ) : (
            <div className="broadcasts-grid">
              {filteredAll.map((b) => (
                <BroadcastCard
                  key={b.id}
                  broadcast={b}
                  programName={
                    multiProgram && b.program_id
                      ? (programNameById.get(b.program_id) ?? null)
                      : null
                  }
                />
              ))}
            </div>
          )}
        </section>
      )}

      {composerOpen && (
        <ComposerModal onClose={() => setComposerOpen(false)} />
      )}
    </div>
  )
}

function EmptyState({ onCompose }: { onCompose: () => void }) {
  return (
    <div className="broadcasts-empty">
      <span className="broadcasts-empty-mark" aria-hidden="true">
        ◷
      </span>
      <p className="broadcasts-empty-title">No broadcasts yet</p>
      <p className="broadcasts-empty-sub">
        Send your first push notification — announce a promo, nudge customers
        who are close to a reward, or wish your birthday customers well.
      </p>
      <button
        type="button"
        className="owner-btn owner-btn-primary"
        onClick={onCompose}
      >
        New broadcast
      </button>
    </div>
  )
}

function BroadcastCard({
  broadcast,
  programName,
}: {
  broadcast: BroadcastRow
  // SLICE-PASS-TYPES — which program this broadcast targeted. Non-null only for
  // a multi-program tenant (where "which program?" is ambiguous); null hides the
  // chip so single-program tenants see no extra noise.
  programName: string | null
}) {
  const cancel = useCancelBroadcast()
  const remove = useRemoveBroadcast()
  const canCancel = broadcast.status === 'pending'
  const canRemove = broadcast.status === 'sent'

  const onCancel = async () => {
    if (!confirm('Cancel this broadcast before it sends?')) return
    try {
      await cancel.mutateAsync(broadcast.id)
      toast.success('Broadcast cancelled')
    } catch (err) {
      const msg =
        err instanceof OwnerApiError ? err.userMessage : 'Cancel failed'
      toast.error(msg)
    }
  }

  const onRemove = async () => {
    if (
      !confirm(
        "Remove this message from your customers' wallet passes? They won't get another notification. The message just disappears from the back of the pass.",
      )
    )
      return
    try {
      await remove.mutateAsync(broadcast.id)
      toast.success(
        'Removal queued. The message will disappear from passes within a minute.',
      )
    } catch (err) {
      const msg =
        err instanceof OwnerApiError ? err.userMessage : 'Removal failed'
      toast.error(msg)
    }
  }

  const assembledBody = assembleBroadcastBody({
    body: broadcast.body,
    validity: {
      validity_kind: broadcast.validity_kind,
      validity_text: broadcast.validity_text,
      validity_start_at: broadcast.validity_start_at,
      validity_end_at: broadcast.validity_end_at,
      validity_includes_time: broadcast.validity_includes_time,
    },
  })

  return (
    <article className="broadcast-card owner-card">
      <div className="broadcast-card-head">
        <span className="broadcast-card-head-left">
          <span className="broadcast-card-audience">
            {displayAudience(broadcast)}
          </span>
          {programName && (
            <span className="broadcast-card-program">{programName}</span>
          )}
        </span>
        <StatusPill status={broadcast.status} />
      </div>

      <p className="broadcast-card-title">{broadcast.title}</p>
      <p className="broadcast-card-body">{assembledBody}</p>

      <div className="broadcast-card-foot">
        <span className="broadcast-card-when">{formatWhen(broadcast)}</span>
        {/* 'everyone' broadcasts post a single class-level message, so
            recipients_total is always 1 (the class, not a customer count) —
            "1 of 1 reached" is noise. Only show the reach count for segmented
            sends, where "12 of 15 reached" is genuinely informative. */}
        {broadcast.segment !== 'everyone' && broadcast.recipients_total > 0 && (
          <span className="broadcast-card-reach">
            {broadcast.recipients_succeeded} of {broadcast.recipients_total}{' '}
            reached
            {broadcast.recipients_failed > 0
              ? ` · ${broadcast.recipients_failed} failed`
              : ''}
          </span>
        )}
      </div>

      {broadcast.status === 'pending' && (
        <p className="broadcast-card-note">
          Waiting for the next send cycle (runs about once a minute). You can
          still cancel before it goes out.
        </p>
      )}
      {broadcast.status === 'sending' && (
        <p className="broadcast-card-note">
          Sending now. Google Wallet usually delivers within seconds.
        </p>
      )}
      {broadcast.status === 'removing' && (
        <p className="broadcast-card-note">
          Removing the message from your customers' passes.
        </p>
      )}
      {broadcast.status === 'removed' && (
        <p className="broadcast-card-note">
          Removed from passes. No second notification was sent.
        </p>
      )}
      {broadcast.status === 'rate_limited' && (
        <p className="broadcast-card-note is-danger">
          {humanizeRateLimitMessage(broadcast.error_message)}
        </p>
      )}
      {broadcast.status === 'failed' && broadcast.error_message && (
        <p className="broadcast-card-note is-danger">
          Couldn't send: {broadcast.error_message}
        </p>
      )}

      {(canCancel || canRemove) && (
        <div className="broadcast-card-actions">
          {canCancel && (
            <button
              type="button"
              className="owner-btn owner-btn-sm"
              onClick={onCancel}
              disabled={cancel.isPending}
            >
              {cancel.isPending ? 'Cancelling…' : 'Cancel before send'}
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              className="owner-btn owner-btn-sm"
              onClick={onRemove}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Removing…' : 'Remove from pass'}
            </button>
          )}
        </div>
      )}
    </article>
  )
}

function StatusPill({ status }: { status: BroadcastStatus }) {
  const label = {
    pending: 'In line',
    sending: 'Sending',
    sent: 'Delivered',
    failed: 'Failed',
    rate_limited: 'Held back',
    removing: 'Removing',
    removed: 'Removed',
  }[status]
  // Tone class drives the colour via CSS — keeps the palette in one place.
  const tone = {
    pending: 'is-muted',
    sending: 'is-active',
    sent: 'is-ok',
    failed: 'is-danger',
    rate_limited: 'is-danger',
    removing: 'is-active',
    removed: 'is-muted',
  }[status]
  return (
    <span className={`broadcast-status-pill ${tone}`}>
      <span className="broadcast-status-dot" aria-hidden="true" />
      {label}
    </span>
  )
}

type ValidityKind = 'text' | 'date' | 'range'

function ComposerModal({ onClose }: { onClose: () => void }) {
  const create = useCreateBroadcast()
  const capabilitiesQ = useBroadcastCapabilities()
  const previewCount = usePreviewCount()
  const programsQ = usePrograms()

  // Which program this broadcast targets (SLICE-PASS-TYPES + MEMBERSHIP). The
  // picker is surfaced when the tenant runs more than one active program of ANY
  // type (loyalty or membership); with one, the broadcast defaults to it
  // server-side. Scopes both the audience and the 'everyone' message to it.
  const activePrograms = (programsQ.data?.programs ?? []).filter(
    (p) =>
      p.status === 'active' &&
      (p.pass_type === 'loyalty' || p.pass_type === 'membership'),
  )
  const multiProgram = activePrograms.length > 1
  const defaultProgram =
    activePrograms.find((p) => p.is_default) ?? activePrograms[0]
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null)
  const effectiveProgramId = selectedProgramId ?? defaultProgram?.id ?? null

  // SLICE-MEMBERSHIP-PASS — the selected program's type drives the audience
  // options. A membership pass has no stamps, so it offers only "Everyone" and
  // (when tiers are enabled) one card per tier — never the stamp-based presets.
  const selectedProgram =
    activePrograms.find((p) => p.id === effectiveProgramId) ?? defaultProgram
  const isMembershipSelected = selectedProgram?.pass_type === 'membership'
  const membershipCfg = isMembershipSelected
    ? (selectedProgram?.type_config as MembershipConfig | null)
    : null
  const membershipTiers: string[] = membershipCfg?.tiers_enabled
    ? (membershipCfg.tiers ?? []).map((t) => t.trim()).filter(Boolean)
    : []

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState<BroadcastAudience>({
    kind: 'everyone',
  })

  // When the selected program changes, drop an audience that no longer applies:
  // a membership program has no stamp segments, and a loyalty program has no
  // tiers. Functional update keyed on the program id so it can't loop.
  useEffect(() => {
    setAudience((prev) => {
      if (isMembershipSelected) {
        if (prev.kind === 'everyone') return prev
        if (
          prev.kind === 'membership_tier' &&
          membershipTiers.includes(prev.tier)
        ) {
          return prev
        }
        return { kind: 'everyone' }
      }
      return prev.kind === 'membership_tier' ? { kind: 'everyone' } : prev
    })
    // membershipTiers + isMembershipSelected are derived from effectiveProgramId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveProgramId])
  // The last resolved count + the audience/program it was computed for. Shown
  // only while it still matches the current selection (cleared on any change).
  const [checked, setChecked] = useState<{
    audience: BroadcastAudience
    programId: string | null
    count: number
  } | null>(null)

  // Slice 5b — optional validity line. Per-kind state stays alive while the
  // toggle is off so toggling back on doesn't lose what the owner typed.
  const [validityOn, setValidityOn] = useState(false)
  const [validityKind, setValidityKind] = useState<ValidityKind>('text')
  const [validityText, setValidityText] = useState('')
  const [validityEndDate, setValidityEndDate] = useState('')
  const [validityEndTime, setValidityEndTime] = useState('')
  const [validityStartDate, setValidityStartDate] = useState('')
  const [validityStartTime, setValidityStartTime] = useState('')
  const [validityIncludeTime, setValidityIncludeTime] = useState(false)

  const validityPayload: BroadcastValidity | null = useMemo(() => {
    if (!validityOn) return null
    if (validityKind === 'text') {
      const t = validityText.trim()
      if (!t || t.length > BROADCAST_VALIDITY_TEXT_MAX) return null
      return { kind: 'text', text: t }
    }
    if (validityKind === 'date') {
      const iso = combineDateTime(
        validityEndDate,
        validityIncludeTime ? validityEndTime : null,
        'end',
      )
      if (!iso) return null
      return { kind: 'date', end_at: iso, includes_time: validityIncludeTime }
    }
    const startIso = combineDateTime(
      validityStartDate,
      validityIncludeTime ? validityStartTime : null,
      'start',
    )
    const endIso = combineDateTime(
      validityEndDate,
      validityIncludeTime ? validityEndTime : null,
      'end',
    )
    if (!startIso || !endIso) return null
    if (new Date(startIso) >= new Date(endIso)) return null
    return {
      kind: 'range',
      start_at: startIso,
      end_at: endIso,
      includes_time: validityIncludeTime,
    }
  }, [
    validityOn,
    validityKind,
    validityText,
    validityEndDate,
    validityEndTime,
    validityStartDate,
    validityStartTime,
    validityIncludeTime,
  ])

  const assembledBody = useMemo(
    () =>
      assembleBroadcastBody({
        body,
        validity: {
          validity_kind: validityPayload?.kind ?? null,
          validity_text:
            validityPayload?.kind === 'text' ? validityPayload.text : null,
          validity_start_at:
            validityPayload?.kind === 'range' ? validityPayload.start_at : null,
          validity_end_at:
            validityPayload?.kind === 'date' ||
            validityPayload?.kind === 'range'
              ? validityPayload.end_at
              : null,
          validity_includes_time:
            (validityPayload?.kind === 'date' ||
              validityPayload?.kind === 'range') &&
            validityPayload.includes_time,
        },
      }),
    [body, validityPayload],
  )

  const titleErr =
    title.length > 50 ? 'Title must be 50 characters or fewer' : null
  const bodyErr =
    assembledBody.length > 500
      ? 'Message plus validity must fit in 500 characters'
      : null

  const validityErr = useMemo<string | null>(() => {
    if (!validityOn) return null
    if (validityPayload === null) {
      if (validityKind === 'text')
        return validityText.trim().length > BROADCAST_VALIDITY_TEXT_MAX
          ? `Validity text must be ${BROADCAST_VALIDITY_TEXT_MAX} characters or fewer`
          : 'Type the validity note'
      if (validityKind === 'date')
        return validityIncludeTime && !validityEndTime
          ? 'Pick an end time'
          : 'Pick an end date'
      return validityStartDate && validityEndDate
        ? 'End must come after start'
        : 'Pick start and end dates'
    }
    return null
  }, [
    validityOn,
    validityPayload,
    validityKind,
    validityText,
    validityIncludeTime,
    validityEndTime,
    validityStartDate,
    validityEndDate,
  ])

  const canSubmit =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    !titleErr &&
    !bodyErr &&
    !validityErr

  // The resolved count is "current" only while it still matches the selected
  // audience AND program — editing either invalidates it (re-check needed).
  const countIsCurrent =
    checked !== null &&
    checked.programId === effectiveProgramId &&
    JSON.stringify(checked.audience) === JSON.stringify(audience)
  // Drives the morphing footer button: null = not yet checked (button is
  // "Check audience"), a number = checked (button becomes "Send to N").
  const sendCount = countIsCurrent ? checked!.count : null

  const onCheckAudience = async () => {
    try {
      const res = await previewCount.mutateAsync({
        audience,
        ...(effectiveProgramId ? { program_id: effectiveProgramId } : {}),
      })
      setChecked({ audience, programId: effectiveProgramId, count: res.count })
    } catch (err) {
      const msg =
        err instanceof OwnerApiError ? err.userMessage : 'Check failed'
      toast.error(msg)
    }
  }

  const submit = async () => {
    if (create.isPending) return
    if (!canSubmit) {
      const reason =
        titleErr ??
        bodyErr ??
        validityErr ??
        (title.trim().length === 0
          ? 'Add a title'
          : body.trim().length === 0
            ? 'Add a message'
            : 'Check the form and try again')
      toast.error(reason)
      return
    }
    const input: BroadcastInput = {
      title: title.trim(),
      body: body.trim(),
      audience,
    }
    if (effectiveProgramId) input.program_id = effectiveProgramId
    if (validityPayload) input.validity = validityPayload

    try {
      await create.mutateAsync(input)
      toast.success(
        'Broadcast queued. It will go out within a minute. Refresh to see status.',
      )
      onClose()
    } catch (err) {
      const msg =
        err instanceof OwnerApiError ? err.userMessage : 'Send failed'
      toast.error(msg)
    }
  }

  return (
    <div
      className="owner-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!create.isPending) onClose()
      }}
    >
      <div
        className="owner-modal broadcasts-composer"
        role="dialog"
        aria-modal="true"
        aria-label="New broadcast"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="broadcasts-composer-head">
          <h3>New broadcast</h3>
          <p className="broadcasts-composer-sub">
            Customers receive a push notification on their phone.
          </p>
        </div>

        <div className="broadcasts-composer-body">
          {/* ---- message ---- */}
          <section className="composer-section">
            <p className="composer-step-label">
              <span className="composer-step-num">1</span> Write your message
            </p>
            <label className="owner-field" style={{ margin: 0 }}>
              <span className="owner-field-label">Title</span>
              <input
                className="owner-input"
                name="broadcast-title"
                autoComplete="off"
                maxLength={60}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Free drink Friday!"
                aria-invalid={titleErr ? 'true' : 'false'}
                autoFocus
              />
              <span className="owner-field-help">
                {title.length} / 50
                {titleErr ? ` · ${titleErr}` : ''}
              </span>
            </label>

            <label className="owner-field" style={{ margin: 0 }}>
              <span className="owner-field-label">Message</span>
              <textarea
                className="owner-textarea"
                name="broadcast-message"
                autoComplete="off"
                rows={4}
                maxLength={600}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Drop by today and grab a free milk tea on us."
                aria-invalid={bodyErr ? 'true' : 'false'}
              />
              <span className="owner-field-help">
                {assembledBody.length} / 500
                {validityOn ? ' (incl. validity line)' : ''}
                {bodyErr ? ` · ${bodyErr}` : ''}
              </span>
            </label>

            <div>
              <label className="broadcast-link-toggle">
                <input
                  type="checkbox"
                  checked={validityOn}
                  onChange={(e) => setValidityOn(e.target.checked)}
                />
                <span>Add a validity line</span>
              </label>
              {validityOn && (
                <div className="broadcast-validity-box">
                  <fieldset className="broadcast-validity-kinds">
                    <label>
                      <input
                        type="radio"
                        name="validityKind"
                        checked={validityKind === 'text'}
                        onChange={() => setValidityKind('text')}
                      />
                      <span>Plain text</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="validityKind"
                        checked={validityKind === 'date'}
                        onChange={() => setValidityKind('date')}
                      />
                      <span>Until a date</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="validityKind"
                        checked={validityKind === 'range'}
                        onChange={() => setValidityKind('range')}
                      />
                      <span>Date range</span>
                    </label>
                  </fieldset>

                  {validityKind === 'text' && (
                    <label className="owner-field" style={{ margin: 0 }}>
                      <input
                        className="owner-input"
                        type="text"
                        maxLength={BROADCAST_VALIDITY_TEXT_MAX + 20}
                        value={validityText}
                        onChange={(e) => setValidityText(e.target.value)}
                        placeholder="while supplies last"
                      />
                      <span className="owner-field-help">
                        Becomes: “Valid: {validityText || 'your note here'}”
                      </span>
                    </label>
                  )}

                  {(validityKind === 'date' || validityKind === 'range') && (
                    <>
                      {validityKind === 'range' && (
                        <div className="broadcast-validity-row">
                          <label
                            className="owner-field"
                            style={{ margin: 0, flex: 1 }}
                          >
                            <span className="owner-field-label">
                              Start date
                            </span>
                            <input
                              className="owner-input"
                              type="date"
                              value={validityStartDate}
                              onChange={(e) =>
                                setValidityStartDate(e.target.value)
                              }
                            />
                          </label>
                          {validityIncludeTime && (
                            <label
                              className="owner-field"
                              style={{ margin: 0, flex: 1 }}
                            >
                              <span className="owner-field-label">
                                Start time
                              </span>
                              <input
                                className="owner-input"
                                type="time"
                                value={validityStartTime}
                                onChange={(e) =>
                                  setValidityStartTime(e.target.value)
                                }
                              />
                            </label>
                          )}
                        </div>
                      )}
                      <div className="broadcast-validity-row">
                        <label
                          className="owner-field"
                          style={{ margin: 0, flex: 1 }}
                        >
                          <span className="owner-field-label">
                            {validityKind === 'range'
                              ? 'End date'
                              : 'Valid until'}
                          </span>
                          <input
                            className="owner-input"
                            type="date"
                            value={validityEndDate}
                            onChange={(e) =>
                              setValidityEndDate(e.target.value)
                            }
                          />
                        </label>
                        {validityIncludeTime && (
                          <label
                            className="owner-field"
                            style={{ margin: 0, flex: 1 }}
                          >
                            <span className="owner-field-label">End time</span>
                            <input
                              className="owner-input"
                              type="time"
                              value={validityEndTime}
                              onChange={(e) =>
                                setValidityEndTime(e.target.value)
                              }
                            />
                          </label>
                        )}
                      </div>
                      <label className="broadcast-link-toggle">
                        <input
                          type="checkbox"
                          checked={validityIncludeTime}
                          onChange={(e) =>
                            setValidityIncludeTime(e.target.checked)
                          }
                        />
                        <span>Include time</span>
                      </label>
                    </>
                  )}

                  {validityErr && (
                    <p className="composer-inline-error">{validityErr}</p>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ---- preview: how it lands on the pass back ---- */}
          <BroadcastBackPreview title={title} body={assembledBody} />

          {/* ---- audience ---- */}
          <section className="composer-section">
            <p className="composer-step-label">
              <span className="composer-step-num">2</span> Choose who receives
              it
            </p>
            {multiProgram && (
              <label className="owner-field" style={{ marginBottom: 12 }}>
                <span className="owner-field-label">Program</span>
                <select
                  className="owner-input"
                  value={effectiveProgramId ?? ''}
                  onChange={(e) => setSelectedProgramId(e.target.value)}
                >
                  {activePrograms.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                <span className="owner-field-help">
                  This broadcast only reaches customers on this program's card.
                </span>
              </label>
            )}
            <AudiencePicker
              audience={audience}
              onChange={(a) => setAudience(a)}
              presetsEnabled={capabilitiesQ.data?.presets_enabled ?? []}
              filterBuilderEnabled={
                capabilitiesQ.data?.filter_builder_enabled ?? false
              }
              loading={capabilitiesQ.isLoading}
              membershipTiers={isMembershipSelected ? membershipTiers : null}
            />
            {audience.kind === 'filter' && (
              <BroadcastFilterBuilder
                match={audience.match}
                rules={audience.rules}
                onChange={(next) => setAudience({ kind: 'filter', ...next })}
              />
            )}
          </section>

          <div className="broadcast-heads-up">
            <p className="broadcast-heads-up-line">
              <strong>On delivery tracking:</strong> Google Wallet doesn't
              report whether a notification was opened — there's no
              delivered/seen metric. We'll add the visits a broadcast drives in
              a future update.
            </p>
            <p className="broadcast-heads-up-line">
              <strong>On notification limits:</strong> Google Wallet caps push
              notifications at 3 per pass per 24 hours, and the customer's phone
              decides how the notification appears. Customers who turned
              notifications off still see the message when they open their
              pass.
            </p>
          </div>
        </div>

        <div className="broadcasts-composer-foot">
          <button
            type="button"
            className="owner-btn"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </button>
          {/* The primary button morphs: it checks the audience first, then
              becomes the send action labelled with the resolved count. */}
          {create.isPending ? (
            <button
              type="button"
              className="owner-btn owner-btn-primary"
              disabled
            >
              Sending…
            </button>
          ) : sendCount === null ? (
            <button
              type="button"
              className="owner-btn owner-btn-primary"
              onClick={onCheckAudience}
              disabled={previewCount.isPending}
            >
              {previewCount.isPending ? 'Checking…' : 'Check audience'}
            </button>
          ) : sendCount === 0 ? (
            <button
              type="button"
              className="owner-btn owner-btn-primary"
              disabled
            >
              No customers match
            </button>
          ) : (
            <button
              type="button"
              className="owner-btn owner-btn-primary"
              onClick={submit}
              aria-disabled={!canSubmit}
            >
              Send to {sendCount} {sendCount === 1 ? 'customer' : 'customers'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/** The audience picker — a grid of selectable segment cards. "Everyone" is
 *  always offered (the floor); the rest come from the tenant's enabled
 *  presets. When the filter builder is unlocked, a final "custom audience"
 *  card opens the rule builder. */
function AudiencePicker({
  audience,
  onChange,
  presetsEnabled,
  filterBuilderEnabled,
  loading,
  membershipTiers,
}: {
  audience: BroadcastAudience
  onChange: (a: BroadcastAudience) => void
  presetsEnabled: BroadcastSegmentKey[]
  filterBuilderEnabled: boolean
  loading: boolean
  // SLICE-MEMBERSHIP-PASS — non-null when the selected program is a membership.
  // A membership pass has no stamps, so the audience is "Everyone" plus one card
  // per tier (empty array = a tier-less membership → Everyone only).
  membershipTiers: string[] | null
}) {
  // Membership variant — Everyone + per-tier cards, no stamp presets / filter.
  if (membershipTiers !== null) {
    return (
      <div className="audience-picker" role="radiogroup" aria-label="Audience">
        <button
          type="button"
          role="radio"
          aria-checked={audience.kind === 'everyone'}
          className={`audience-card${
            audience.kind === 'everyone' ? ' is-selected' : ''
          }`}
          onClick={() => onChange({ kind: 'everyone' })}
        >
          <span className="audience-card-check" aria-hidden="true" />
          <span className="audience-card-text">
            <span className="audience-card-label">Everyone</span>
            <span className="audience-card-desc">
              Every active member who has your pass.
            </span>
          </span>
        </button>
        {membershipTiers.map((tier) => {
          const selected =
            audience.kind === 'membership_tier' && audience.tier === tier
          return (
            <button
              key={tier}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`audience-card${selected ? ' is-selected' : ''}`}
              onClick={() => onChange({ kind: 'membership_tier', tier })}
            >
              <span className="audience-card-check" aria-hidden="true" />
              <span className="audience-card-text">
                <span className="audience-card-label">{tier} tier</span>
                <span className="audience-card-desc">
                  Members on the {tier} tier.
                </span>
              </span>
            </button>
          )
        })}
      </div>
    )
  }

  // "Everyone" first and always; then every other enabled segment, de-duped.
  const keys: BroadcastSegmentKey[] = [
    'everyone',
    ...presetsEnabled.filter((k) => k !== 'everyone'),
  ]
  const selectedKey: BroadcastSegmentKey | null =
    audience.kind === 'everyone'
      ? 'everyone'
      : audience.kind === 'birthday_this_month'
        ? 'birthday_this_month'
        : audience.kind === 'preset'
          ? audience.preset
          : null

  return (
    <div className="audience-picker" role="radiogroup" aria-label="Audience">
      {keys.map((key) => {
        const meta = segmentMeta(key)
        const selected = selectedKey === key
        return (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`audience-card${selected ? ' is-selected' : ''}`}
            onClick={() => onChange(audienceForKey(key))}
          >
            <span className="audience-card-check" aria-hidden="true" />
            <span className="audience-card-text">
              <span className="audience-card-label">{meta.label}</span>
              <span className="audience-card-desc">{meta.description}</span>
            </span>
          </button>
        )
      })}
      {filterBuilderEnabled && (
        <button
          type="button"
          role="radio"
          aria-checked={audience.kind === 'filter'}
          className={`audience-card audience-card-custom${
            audience.kind === 'filter' ? ' is-selected' : ''
          }`}
          onClick={() => {
            // Don't clobber an in-progress filter when re-clicking the card.
            if (audience.kind !== 'filter') {
              onChange({
                kind: 'filter',
                match: 'all',
                rules: [defaultRule('stamps')],
              })
            }
          }}
        >
          <span className="audience-card-check" aria-hidden="true" />
          <span className="audience-card-text">
            <span className="audience-card-label">Build a custom audience</span>
            <span className="audience-card-desc">
              Combine your own filters — stamps, recency, rewards and more.
            </span>
          </span>
        </button>
      )}
      {loading && keys.length === 1 && (
        <p className="audience-picker-loading">Loading your segments…</p>
      )}
    </div>
  )
}

/** A lightweight preview of how the broadcast lands on the back of the pass —
 *  title + assembled body, framed as the wallet message block. */
function BroadcastBackPreview({
  title,
  body,
}: {
  title: string
  body: string
}) {
  const hasContent = title.trim().length > 0 || body.trim().length > 0
  return (
    <div className="broadcast-back-preview" aria-label="Pass preview">
      <div className="bbp-frame">
        <div className="bbp-notch" aria-hidden="true" />
        <p className="bbp-eyebrow">Back of the wallet pass</p>
        <div className="bbp-message">
          <span className="bbp-message-tag">Message</span>
          {hasContent ? (
            <>
              <p className="bbp-message-title">
                {title.trim() || 'Your title'}
              </p>
              <p className="bbp-message-body">
                {body.trim() || 'Your message will appear here.'}
              </p>
            </>
          ) : (
            <p className="bbp-message-empty">
              Start typing — your broadcast preview appears here.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Slice 5b — combine a YYYY-MM-DD date input with an optional HH:MM time into
 * a UTC ISO string. No time given → start-of-day 00:00 / end-of-day 23:59
 * local. Returns null when the date is empty or invalid.
 */
function combineDateTime(
  date: string,
  time: string | null,
  edge: 'start' | 'end',
): string | null {
  if (!date) return null
  const t = time ? time : edge === 'end' ? '23:59' : '00:00'
  const local = new Date(`${date}T${t}:00`)
  if (Number.isNaN(local.getTime())) return null
  return local.toISOString()
}

/**
 * Rate-limited broadcasts now only ever come from an admin hard cap (the
 * blocking soft nudge was removed). Swap raw ISO timestamps for friendly
 * local-time strings; fall back to the raw message if the shape is unexpected.
 */
function humanizeRateLimitMessage(raw: string | null): string {
  if (!raw)
    return 'Held back. Your tenant has reached its broadcast limit. Wait for the window to clear.'
  return raw.replace(
    /\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z\b/g,
    (iso) =>
      new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
  )
}

function formatWhen(b: BroadcastRow): string {
  const ts = b.completed_at ?? b.started_at ?? b.scheduled_for ?? b.created_at
  return new Date(ts).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

interface CapShape {
  per_day: number | null
  per_week: number | null
  sent_last_day: number
  sent_last_week: number
  remaining_today: number | null
  remaining_this_week: number | null
}

/**
 * The cadence line shown above the list. Admin hard caps (per_day/per_week)
 * are a real ceiling — surfaced as remaining quota. With no hard cap, the
 * 7-day signal is advisory only: it never blocks a send (the blocking soft
 * nudge was removed), so the copy is a gentle nudge, not a warning.
 */
function formatCapMessage(cap: CapShape): string {
  const parts: string[] = []
  if (cap.per_week !== null) {
    parts.push(
      `${cap.remaining_this_week} of ${cap.per_week} left this week`,
    )
  }
  if (cap.per_day !== null) {
    parts.push(`${cap.remaining_today} of ${cap.per_day} left today`)
  }
  if (parts.length > 0) {
    return `Broadcast limit: ${parts.join(' · ')}.`
  }
  if (cap.sent_last_week >= 1) {
    return `${cap.sent_last_week} broadcast${cap.sent_last_week === 1 ? '' : 's'} sent in the last 7 days. Consider giving customers some breathing room.`
  }
  return 'No broadcast sent in the last 7 days.'
}
